import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { valueRender } from '@repo/toolkit/conditional-render'
import { DateFormatter } from '@repo/toolkit/date'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Badge from '@repo/ui/badge'
import Box from '@repo/ui/box'
import Button from '@repo/ui/button'
import DataTable, { Column } from '@repo/ui/data-table'
import { BusIcon, PlusIcon, PrintIcon, TravelTwoIcon } from '@repo/ui/icons'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { API, constructApiUrl } from '../../api/apis.config'
import { assignJourneysToRad } from '../../api/rads/assign-journeys-to-rad'
import { getRad, getRadCenters } from '../../api/rads/get-rad'
import { printRadPli } from '../../api/rads/print-rad-pli'
import { updateRad } from '../../api/rads/update-rad'
import CountryFlag from '../../components/elements/country-flag'
import { DynamicTableRef } from '../../components/elements/dynamic-table'
import InformationToolTip from '../../components/elements/information-tooltip'
import { ItemNotFound } from '../../components/elements/item-not-found'
import LoadingScreen from '../../components/elements/loading/loading-screen'
import UserNavBar from '../../components/elements/nav-bar'
import PeriodBadge from '../../components/elements/period-badge'
import JourneyListSelection from '../../components/pages/enrollments/enrolment-create-proposition-steps/journey-list-selection'
import JourneyListSelectionFilter from '../../components/pages/enrollments/enrolment-create-proposition-steps/journey-list-selection-filter'
import PrintPliButton from '../../components/pages/rads/print-pli-button'
import RadForm from '../../components/pages/rads/rad-form'
import RadStaffTable from '../../components/pages/rads/rad-staff-table'
import UnassignJourneyFromRadButton from '../../components/pages/rads/unassign-journey-from-rad-button'
import { Paths } from '../../lib/constants/path'
import { useDownloadDocuments } from '../../lib/hooks/use-download-documents'
import { useFilters } from '../../lib/hooks/use-filters'
import { useModalManager } from '../../lib/hooks/use-modal-manager'
import { accomodationCategoryEnumMapping } from '../../lib/types/enums/product/accomodation-category.enum'
import { CenterRad } from '../../lib/types/models/center/center'
import { Journey, JourneyFilters, JourneyPartial } from '../../lib/types/models/product/journey'
import { RadParticipant } from '../../lib/types/models/rad/rad-participant'
import { updateButtonsConfig } from '../../lib/utils/buttons-config/validation-buttons-config'
import {
  transformJourneyFiltersToUrl,
  transformUrlToJourneyFilters
} from '../../lib/utils/filters-mapping/journey'
import { showAxiosError } from '../../lib/utils/functions/misc.functions'
import { getTranslatedName } from '../../lib/utils/functions/translation.functions'
import NotFoundPage from '../common/not-found'
import { constructOlCenterPath } from '../ols/center/utils/functions'

const RadUpdatePage = () => {
  const [selectedJourneys, setSelectedJourneys] = useState<Journey[]>([])
  const { navigate, back } = useNavigator()
  const { downloadDocuments } = useDownloadDocuments()

  const { modalState, toggleModal } = useModalManager<'addJourney'>(['addJourney'])

  const journeyFilter = useFilters<JourneyFilters>({
    initialFilters: { published: true, withoutRad: true },
    options: { allowSearchParam: false },
    transformFilterToUrl: transformJourneyFiltersToUrl,
    transformUrlToFilter: transformUrlToJourneyFilters
  })

  const { id } = useParams<{ id: string }>()

  if (!id || isNaN(parseInt(id)))
    return <ItemNotFound title="Identifiant de Réunion avant départ (RAD) invalide" />

  const {
    data: rad,
    isLoading: loadingRAD,
    refetch
  } = useQuery({
    queryKey: ['rad', id],
    queryFn: () => getRad(+id)
  })

  const {
    data: centers,
    isLoading: loadingCenters,
    refetch: refetchCenters
  } = useQuery({ queryKey: ['rad-centers', id], queryFn: () => getRadCenters(+id) })

  const staffTableRef = useRef<DynamicTableRef<RadParticipant>>(null)

  const updateRadMutation = useMutation({
    mutationFn: updateRad,
    onError: showAxiosError,
    onSuccess() {
      refetch()
      toast.success('Réunion avant départ (RAD) modifiée avec succès')
    }
  })

  const printRadPliMutation = useMutation({
    mutationFn: printRadPli,
    onError: showAxiosError
  })

  const assignJourneysMutation = useMutation({
    mutationFn: assignJourneysToRad,
    onError: showAxiosError,
    onSuccess() {
      refetch()
      refetchCenters()
      staffTableRef.current?.refetch()
      setSelectedJourneys([])
      toggleModal('addJourney')
      toast.success(`${selectedJourneys.length} séjour(s) assigné(s) à la RAD`)
    }
  })

  if (loadingRAD || loadingCenters) return <LoadingScreen />
  if (!rad) return <NotFoundPage />

  const journeyColumns: Column<JourneyPartial>[] = [
    {
      header: 'Code Séjour',
      accessorKey: 'reference',
      enableSorting: true
    },
    {
      header: 'Pays',
      accessorKey: 'date.product.country.name',
      enableSorting: true,
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <CountryFlag code={row.original.date.product.country.code} />
          <span>{row.original.date.product.country.name}</span>
        </div>
      )
    },
    {
      header: 'Formule',
      accessorKey: 'date.product.formula.title',
      enableSorting: true,
      cell: ({ row }) => getTranslatedName(row.original.date.product.formula)
    },
    {
      header: "Type d'hébergement",
      accessorKey: 'date.product.accomodationCategory',
      enableSorting: true,
      cell: ({ row }) => {
        return valueRender(
          row.original.date.product.accomodationCategory,
          (category) => accomodationCategoryEnumMapping[category]
        )
      }
    },
    {
      header: 'Période',
      accessorKey: 'date.period.name',
      enableSorting: true,
      cell: ({ row }) => <PeriodBadge period={row.original.date.period} />
    },
    {
      header: 'Date',
      accessorKey: 'date.departureDate',
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        const dateA = new Date(rowA.original.date.departureDate).getTime()
        const dateB = new Date(rowB.original.date.departureDate).getTime()
        if (!dateA || !dateB) return 0
        return dateA - dateB
      },
      cell: ({ row }) => (
        <Badge
          text={`${DateFormatter.DDMMYYYY(row.original.date.departureDate)} - ${DateFormatter.DDMMYYYY(row.original.date.returnDate)}`}
        />
      )
    },
    {
      header: '',
      accessorKey: 'id',
      cell: ({ row }) => (
        <UnassignJourneyFromRadButton
          id={parseInt(id)}
          journeyId={row.original.id}
          onSuccess={() => {
            refetch()
            refetchCenters()
            staffTableRef.current?.refetch()
            toast.success('Séjour désassigné de la RAD avec succès')
          }}
        />
      )
    }
  ]

  const centerColumns: Column<CenterRad>[] = [
    {
      header: 'Nom du centre',
      accessorKey: 'name',
      cell: ({ row }) => {
        const ol = row.original.ol
        const text = ol ? (
          <Typography.Link
            onClick={() => {
              navigate(constructOlCenterPath(Paths.OL_CENTER_PLACEMENTS, row.original.id), {
                state: { from: Paths.RAD_UPDATE_PAGE.replace(':id', id) }
              })
            }}
            variant="secondary-body"
          >
            {row.original.name}
          </Typography.Link>
        ) : (
          <Typography variant="secondary-body">{row.original.name}</Typography>
        )

        return (
          <div className="w-full flex items-center justify-between pr-2">
            {text}
            <PrintPliButton
              pli={{
                path: row.original.pli?.path,
                pliModifiedAt: row.original.pliModifiedAt,
                pliPrintedAt: row.original.pliPrintedAt
              }}
              type="center"
              centerId={row.original.id}
              radId={parseInt(id)}
              onSuccess={() => refetchCenters()}
            />
          </div>
        )
      }
    }
  ]

  return (
    <div>
      <UserNavBar title={'Réunion avant départ (RADs)'} icon={BusIcon} />
      <div className={'h-4'} />

      <div className="space-y-4">
        <Box>
          <div className="flex justify-end items-center gap-3 pb-4">
            {centers?.total === 0 && (
              <InformationToolTip variant="warning" size="small" align="end">
                <Typography variant="label" color={tailwindColors.white.active} className="w-64">
                  Aucun centre n'est rattaché aux séjours de cette RAD, il n'y a donc aucun pli à
                  imprimer.
                </Typography>
              </InformationToolTip>
            )}
            <Button
              variant="ghost"
              label="Imprimer tous les plis"
              icon={PrintIcon}
              disabled={!centers?.total}
              loading={printRadPliMutation.isPending}
              onClick={() => printRadPliMutation.mutate(parseInt(id))}
            />

            <Button
              variant="ghost"
              label="Imprimer la feuille d'émargement"
              icon={PrintIcon}
              onClick={async () =>
                await downloadDocuments({
                  endpoint: constructApiUrl(API.rads.printAttendanceSheet.url, {
                    id: parseInt(id)
                  })
                })
              }
            />

            <Button
              variant="ghost"
              label="Imprimer les fiches nominatives"
              icon={PrintIcon}
              onClick={async () => {
                await downloadDocuments({
                  endpoint: constructApiUrl(API.rads.printNameCards.url, { id: parseInt(id) })
                })
              }}
            />
          </div>
          <RadForm
            defaultValues={rad}
            buttonsConfig={updateButtonsConfig({
              onCancel: () => back(Paths.RAD_LIST_PAGE),
              onUpdate: (values) => updateRadMutation.mutateAsync({ id: parseInt(id), ...values }),
              onUpdateThenCreate: async (values) => {
                await updateRadMutation.mutateAsync({ id: parseInt(id), ...values })
                navigate(Paths.RAD_CREATE_PAGE)
              },
              onUpdateThenList: async (values) => {
                await updateRadMutation.mutateAsync({ id: parseInt(id), ...values })
                back(Paths.RAD_LIST_PAGE)
              }
            })}
          />
        </Box>

        <Box className="flex flex-col gap-4 py-5">
          <div className="flex items-center gap-4">
            <Typography variant="sub-heading">Séjours</Typography>
            <Button
              variant="primary"
              label="Ajouter un séjour"
              icon={PlusIcon}
              onClick={(e) => {
                e.stopPropagation()
                toggleModal('addJourney')
              }}
            />
          </div>

          <DataTable<JourneyPartial>
            rowCount={rad.journeys?.length ?? 0}
            data={rad.journeys ?? []}
            disableHover
            manualSorting={false}
            limitControl={false}
            paginated={false}
            columns={journeyColumns}
          />
        </Box>

        <Box className="flex flex-col gap-4 py-5">
          <Typography variant="sub-heading">Centres</Typography>

          <DataTable<CenterRad>
            rowCount={centers?.total ?? 0}
            data={centers?.data ?? []}
            disableHover
            limitControl={false}
            paginated={false}
            columns={centerColumns}
          />
        </Box>

        <RadStaffTable radId={parseInt(id)} tableRef={staffTableRef} />
      </div>

      <Modal
        isOpen={modalState.addJourney}
        title="Assigner un séjour"
        titleAlignment="left"
        size="large"
        hideActions
        icon={TravelTwoIcon}
        onCancel={() => toggleModal('addJourney')}
      >
        <div className={`space-y-4 ${MODAL_BODY_CLASSNAME}`}>
          <JourneyListSelectionFilter
            filter={journeyFilter.filter}
            onResetFilter={journeyFilter.resetFilters}
            onSelectedFilterChange={journeyFilter.onSelectedFilterChange}
          />
          <JourneyListSelection
            tableTitle="Sélectionner un ou plusieurs séjour(s)"
            mode="multiple"
            filterProps={journeyFilter}
            isLoading={assignJourneysMutation.isPending}
            onSubmit={async () => {
              if (selectedJourneys.length > 0) {
                await assignJourneysMutation.mutateAsync({
                  id: parseInt(id),
                  journeyIds: selectedJourneys.map((journey) => journey.id)
                })
              }
            }}
            onSelectJourneys={setSelectedJourneys}
          />
        </div>
      </Modal>
    </div>
  )
}

export default RadUpdatePage
